import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { withSitecoreContext, Placeholder, ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { windowService } from '../../utils/window.utils';
import { TLinkField } from '../../types/types';
import CarouselArrow from '../@shared/carouselArrow';
import appendDots from '../@shared/customCarouselDots/appendDots';
import Heading from '../@shared/heading';
import CarouselSlide from './components/carouselSlide';
import { TSpecialOffersProps } from './types/cardCarousel.types';
import { SpecialOffersModel, SpecialOffersSlideModel } from './models/cardCarousel.model';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { googleClickSlideEvent, formatComponentNumber } from '../../utils/analytics';
import { getCustomPaging } from '../@shared/carouselDots';

const CardCarousel = (props: TSpecialOffersProps): JSX.Element => {
  const isEE = props?.sitecoreContext?.pageEditing;
  const model = new SpecialOffersModel(props);
  const cardsCount = (model?.items || []).length;
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [screenWidth, setScreenWidth] = useState(1680);
  const [activeCardIndex, updateActiveIndex] = useState(0);
  const [isClickedOnArrow, setIsClickedOnArrow] = useState(false);
  const containerPaddingLeft = 32;
  const largeImageWidth = isDesktop ? 664 : 300;
  const smallImageWidth = isDesktop ? 392 : 300;

  const totalElementsWidth =
    model?.items?.reduce((sumWidths: number, curr: SpecialOffersSlideModel) => {
      if (curr?.type?.value === 'offerCardWide') {
        sumWidths += largeImageWidth;
      } else {
        sumWidths += smallImageWidth;
      }

      return sumWidths;
    }, 0) + containerPaddingLeft;
  const shouldInstanceCarousel = totalElementsWidth > screenWidth;

  const arrowButtonClick = (): void => {
    setIsClickedOnArrow(true);
  };

  const settings = {
    infinite: shouldInstanceCarousel,
    dots: shouldInstanceCarousel,
    arrows: !isEE && shouldInstanceCarousel,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: <CarouselArrow direction="next" onClickSecondary={arrowButtonClick} />,
    prevArrow: <CarouselArrow direction="previous" onClickSecondary={arrowButtonClick} />,
    customPaging: getCustomPaging,
    afterChange: (index): void => {
      updateActiveIndex(index);
    },
    appendDots: (dots) => appendDots({ dots, cardsCount, activeCardIndex, showCustomDots: cardsCount > 5 }),
    dotsClass: `${cardsCount <= 5 ? 'slick-dots' : ''}`,
  };

  useEffect(() => {
    setScreenWidth(Math.min(windowService()?.innerWidth, 1680));
  }, []);

  useEffect(() => {
    if (!isClickedOnArrow) {
      return;
    }

    setIsClickedOnArrow(false);

    googleClickSlideEvent(
      `Slide ${formatComponentNumber(activeCardIndex + 1)}`,
      model.items[activeCardIndex]?.title?.value || model.eyebrow?.value,
      activeCardIndex + 1
    );
  }, [activeCardIndex]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <AnalyticsVisibleEvent
      className="card-carousel special-offers-wrapper"
      styles={{ backgroundColor: model.backgroundColor || '#fff' }}
      rendering={props?.rendering}
      title={model.title?.value || model.eyebrow?.value}
      template="Card Carousel"
    >
      <div className="special-offers">
        <Heading
          eyebrow={model.eyebrow}
          title={model.title}
          subtitle={model.subtitle}
          link={model?.link as TLinkField}
          editable={true}
          customTextTitle
          tag={model.headingTag}
          useAnalytics
        >
          <div className="special-offers__pop-up">
            <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
          </div>
        </Heading>
        <Slider {...settings}>
          {model?.items?.map((item, index) => (
            <div key={index} className="special-offers__slide">
              <CarouselSlide
                slideIndex={index}
                model={item}
                fallbackAnalyticsTitle={model.eyebrow?.value}
                isEE={isEE}
                cardHeadingTag={model?.cardHeadingTitleTag}
              />
            </div>
          ))}
        </Slider>
        <div className="special-offers__footer"></div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(CardCarousel);
